import { useMutation, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/**
 * La Compuerta 1: aprobar o rechazar el precio.
 *
 * VIVE AQUÍ Y NO EN EL TABLERO, y ese fue justamente el problema que reportó
 * MEGA: «desde cotización no tengo cómo aceptar para pasar a programación».
 * `fn_decidir_cotizacion` tenía UN solo llamador en toda la aplicación —la
 * tarjeta de decisiones rápidas del tablero— así que la única compuerta del
 * recorrido solo se podía cruzar desde una pantalla que no es donde está el
 * documento. Peor: la tarjeta de la matriz decía «Esperando su decisión» y al
 * tocarla llevaba al detalle de la orden, que era el único sitio sin el botón.
 *
 * Al aprobar nace sola la orden de trabajo, por el disparador de la base. Por
 * eso se refresca todo el árbol de órdenes y no solo la cotización.
 */
export function useDecidirCotizacion() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      aprobar,
      notas,
    }: {
      cotizacionId: string
      aprobar: boolean
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_decidir_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_aprobar: aprobar,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: () => refrescarTodo(cliente),
  })
}

/**
 * DEVOLVERLA AL TÉCNICO (0302), con lo que le faltó. Solo mientras no haya
 * salido al cliente; vuelve a borrador y le reaparece en «Para cotizar».
 */
export function useDevolverCotizacion() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({ cotizacionId, motivo }: { cotizacionId: string; motivo: string }) => {
      const { error } = await supabase.rpc('fn_devolver_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_motivo: motivo,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: () => refrescarTodo(cliente),
  })
}

/**
 * Aprobar y ENVIÁRSELA AL CLIENTE, que es quien de verdad decide.
 *
 * Antes gerencia aprobaba y en ese mismo acto nacía la orden de trabajo: se
 * programaba un técnico para un trabajo que el cliente todavía no había
 * aceptado. Ahora la compuerta tiene dos tiempos —gerencia da el visto bueno
 * interno, el cliente acepta desde su enlace— y la orden de trabajo nace con
 * la aceptación del cliente, no antes.
 *
 * Devuelve el token: con él se arma el enlace que se le manda y que la oficina
 * puede copiar si el cliente lo perdió.
 */
export function useEnviarCotizacionAlCliente() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async (cotizacionId: string): Promise<EnvioAlCliente> => {
      const { data, error } = await supabase.rpc('fn_enviar_cotizacion_al_cliente', {
        p_cotizacion_id: cotizacionId,
      })

      if (error) throw new Error(mensajeDeError(error))
      const token = String(data ?? '')

      // Y sale por WhatsApp de una. El cron también la recogería, pero un
      // minuto mirando la pantalla sin que pase nada se lee como que falló.
      //
      // Si WhatsApp no la deja salir NO se cae la operación: el enlace ya
      // existe y hay que enseñarlo igual. Perder el token porque Meta estaba
      // caído obligaría a rehacer la aprobación por nada.
      //
      // PERO SE LEE EL RESULTADO. Antes se descartaba, y la pantalla decía
      // «le llega por WhatsApp» tanto si había salido como si no: peor que no
      // avisar, porque nadie va a copiar un enlace que cree que ya salió.
      const { data: envio, error: falloEnvio } = await supabase.functions.invoke(
        'enviar-cotizacion',
        { body: { cotizacion_id: cotizacionId } },
      )

      let salio = !falloEnvio && Number((envio as { enviados?: number })?.enviados ?? 0) > 0

      /*
       * SI LA LLAMADA FALLA, LA RESPUESTA NO ES LA VERDAD: LO ES LA BASE.
       *
       * Pasó con la cotización de Yolanda: la pantalla dijo «no se pudo
       * enviar» y el WhatsApp salió catorce segundos después. La llamada
       * directa se puede caer por mil motivos —la red del iPad, un tiempo de
       * espera, la función arrancando en frío— y además el barrido
       * automático recoge al minuto lo que quedó pendiente. Juzgar por la
       * respuesta inmediata es juzgar por el mensajero.
       *
       * Así que cuando la llamada no confirma, se le pregunta a la
       * cotización: si tiene fecha de envío, salió. Y si no, se dice lo que
       * de verdad pasa —va en camino— en vez de dar por muerto algo vivo.
       */
      let motivo: string | null = salio
        ? null
        : (envio as { nota?: string; error?: string })?.nota ??
          (envio as { error?: string })?.error ??
          falloEnvio?.message ??
          null

      if (!salio) {
        /*
         * SE ESPERA DE VERDAD, NO UN SEGUNDO Y MEDIO.
         *
         * COT-2026-0021 (05/10): el navegador perdió la respuesta, la base se
         * miró a los 1,5 s y la pantalla dijo «el WhatsApp no salió»… que
         * había salido: la función tardó 18 s (lee la plantilla en Meta y
         * manda) y el mensaje llegó a las 4:43:12. Se pregunta cada 2 s hasta
         * 30 s; lo que pase de ahí sí es para avisar.
         */
        let fila: {
          enviada_al_cliente_en: string | null
          envio_cliente_error: string | null
        } | null = null
        for (let vuelta = 0; vuelta < 15; vuelta++) {
          await new Promise((seguir) => setTimeout(seguir, 2000))
          const { data } = await supabase
            .from('cotizaciones')
            .select('enviada_al_cliente_en, envio_cliente_error')
            .eq('id', cotizacionId)
            .maybeSingle()
          fila = data
          if (fila?.enviada_al_cliente_en || fila?.envio_cliente_error) break
        }

        if (fila?.enviada_al_cliente_en) {
          salio = true
          motivo = null
        } else if (fila?.envio_cliente_error) {
          // El motivo de la base manda: es el que dio WhatsApp.
          motivo = fila.envio_cliente_error
        } else if (!motivo) {
          motivo =
            'Todavía no sale confirmado. El reenvío automático lo intenta en un minuto; si no, use el enlace.'
        }
      }

      return { token, salio, motivo }
    },
    onSuccess: () => refrescarTodo(cliente),
  })
}

/** Lo que hay que saber después de enviarla: el enlace, y si de verdad salió. */
export type EnvioAlCliente = {
  token: string
  salio: boolean
  motivo: string | null
}

/** El enlace que ve el cliente. La página es estática y vive en el sitio. */
export function enlaceDeCotizacion(token: string): string {
  return `${window.location.origin}/cotizacion.html?t=${encodeURIComponent(token)}`
}

/**
 * Volver a pedirle a World Office una cotización que no quiso crear.
 *
 * Manda los mismos renglones del intento anterior: volver a teclear once
 * renglones porque a World Office le faltaba un permiso es trabajo que no le
 * toca a nadie.
 */
export function useReintentarCotizacion() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async (cotizacionId: string): Promise<void> => {
      const { error } = await supabase.rpc('fn_reintentar_cotizacion', {
        p_cotizacion_id: cotizacionId,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: () => refrescarTodo(cliente),
  })
}

/**
 * Decidir mueve la orden de estación, hace nacer la orden de trabajo y cambia
 * lo que espera en el tablero. Refrescar solo la cotización dejaría el resto
 * de la pantalla contando una historia vieja.
 *
 * LAS CLAVES SON LAS QUE EXISTEN, letra por letra. TanStack compara elemento a
 * elemento: invalidar ['orden'] no alcanza a ['ordenes-servicio', 'detalle', id]
 * y la pantalla se queda 30 s contando lo de antes. Pasó. No hay registro
 * central de claves: antes de invalidar, buscar la queryKey exacta con grep.
 */
export async function refrescarTodo(cliente: ReturnType<typeof useQueryClient>) {
  await Promise.all(
    [
      'ordenes-servicio', // la matriz y el detalle
      'pendientes-de-decision', // los contadores naranjas del menú
      'lo-que-espera', // el tablero
      'indicadores-cotizacion',
      'cuello-de-botella',
      'cronograma',
      'recorridos', // el tablero de recorrido (todas)
      'recorrido', // una orden en su recorrido
      'cotizaciones-wo',
      'avisos',
      'mi-trabajo', // la pantalla del técnico
    ].map((clave) => cliente.invalidateQueries({ queryKey: [clave] })),
  )
}

/**
 * DAR POR ACEPTADA UNA COTIZACIÓN QUE EL CLIENTE ACEPTÓ POR FUERA.
 *
 * El cliente dice «arranquen» por teléfono o en la obra y el trabajo no puede
 * quedarse esperando un clic en un enlace que muchas veces ni abre.
 *
 * Pide el MEDIO a propósito: es lo único que distingue «el cliente aceptó por
 * teléfono» de una aprobación a dedo, y es lo que se lee dentro de seis meses
 * si el cliente discute. El sello del clic real —decidida_por_cliente_en— no se
 * toca: esto no fue un clic suyo y el papel no puede decir que sí.
 */
export function useRegistrarAceptacion() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      medio,
      notas,
    }: {
      cotizacionId: string
      medio: string
      notas?: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_registrar_aceptacion_del_cliente', {
        p_cotizacion_id: cotizacionId,
        p_medio: medio,
        p_notas: notas ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: () => refrescarTodo(cliente),
  })
}
